// AccessApprovalView — the access-approval task body: Luna recommendation,
// the requester's person card, and the resource card (requested-for, type,
// prior requests, team adoption, comment), then the roles the request names.
// design ref: drawers.jsx DrawerDetailsBody (access_request branch) +
// AccessRequestResource.

import { Box, Icon, Text } from "@chakra-ui/react";
import { TbClock } from "react-icons/tb";

import Chip from "components/ds/Chip";
import LunaCard from "components/ds/LunaCard";
import SectionHeader from "components/ds/SectionHeader";
import RequestLines from "pages/Members/Access/Catalogs/RequestLines";
import type { MFAccessApprovalEntity } from "state/members/tasks";

import LunaInsight from "./LunaInsight";
import PersonCard from "./PersonCard";
import PriorRequests from "./PriorRequests";
import ResourceDetailCard, { ResourceRow } from "./ResourceDetailCard";
import TeamAdoption from "./TeamAdoption";
import { approvalResourceName, formatDuration } from "./taskMeta";

const Section = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <Box>
    <SectionHeader>{label}</SectionHeader>
    {children}
  </Box>
);

const AccessApprovalView = ({ entity }: { entity: MFAccessApprovalEntity }) => {
  const resourceName =
    approvalResourceName(entity.selectableResource) ?? "Resource";
  const resourceImage =
    entity.selectableResource?.imageUrl ||
    entity.selectableResource?.resource?.imageUrl;

  return (
    <>
      {entity.aiInsight && (
        <LunaCard>
          <LunaInsight text={entity.aiInsight} />
        </LunaCard>
      )}

      <Section label="Requested by">
        <PersonCard
          member={entity.requester}
          identity={entity.requestedByIdentity}
          showHrContext
        />
      </Section>

      <Section label="Resource">
        <ResourceDetailCard
          name={resourceName}
          imageUrl={resourceImage}
          catalogItemName={entity.catalogItemName}
          catalogName={entity.catalogName}
        >
          {!entity.lines?.length && (
            <ResourceRow label="Requested for">
              <Chip icon={<Icon as={TbClock} boxSize="12px" />}>
                {formatDuration(
                  entity.requestedForSeconds,
                  entity.requestedIndefinite,
                )}
              </Chip>
            </ResourceRow>
          )}
          {entity.requestedIdentity && (
            <ResourceRow label="Account">
              <Chip
                icon={
                  entity.requestedIdentity.intImageUrl ? (
                    <Box
                      as="img"
                      src={entity.requestedIdentity.intImageUrl}
                      boxSize="12px"
                      borderRadius="2px"
                    />
                  ) : undefined
                }
              >
                {entity.requestedIdentity.username ||
                  entity.requestedIdentity.displayName ||
                  entity.requestedIdentity.extId}
              </Chip>
            </ResourceRow>
          )}
          <ResourceRow label="Type">
            <Chip tone="azurite">Approval required</Chip>
          </ResourceRow>
          <ResourceRow label="Prior requests">
            <PriorRequests data={entity.priorRequests} />
          </ResourceRow>
          {entity.teamAdoption && (
            <ResourceRow label="Team adoption" alignTop>
              <TeamAdoption data={entity.teamAdoption} />
            </ResourceRow>
          )}
          {entity.requestComments && (
            <ResourceRow label="Comment">
              <Text as="span" fontWeight={400} color="neutral.800">
                {entity.requestComments}
              </Text>
            </ResourceRow>
          )}
        </ResourceDetailCard>
      </Section>

      {entity.lines?.length ? (
        <Section label="Roles">
          <Text variant="lil" mb={2}>
            Your decision covers every role. &quot;This stage&quot; marks the
            roles this approval step is for.
          </Text>
          <RequestLines lines={entity.lines} showStatus={false} />
        </Section>
      ) : null}
    </>
  );
};

export default AccessApprovalView;
